import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useState } from 'react';

const QUICK_SUMMARY = [
  'Excel values override existing values when provided',
  'Blank cells do not clear data',
  'Google Books helps with new books, but is not required',
  'AI tags V2 use the final merged book data',
];

const BulkImportHelp = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="rounded-lg border border-gray-200 bg-bg-surface">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50"
        aria-expanded={isOpen}
      >
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary-50 p-2 text-primary-600">
            <Info className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-text-primary">Bulk Import: What to Expect</h2>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-text-secondary">
                Help
              </span>
            </div>
            <p className="mt-1 text-sm text-text-secondary">
              See how Bukito handles updates, new books, Google Books lookups, enrichment, and AI tags.
            </p>
          </div>
        </div>

        <div className="mt-1 text-text-secondary">
          {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
      </button>

      <div className="border-t border-gray-200 px-5 py-4">
        <div className="flex flex-wrap gap-2">
          {QUICK_SUMMARY.map((item) => (
            <span
              key={item}
              className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700"
            >
              {item}
            </span>
          ))}
        </div>

        {isOpen && (
          <div className="mt-5 space-y-6 text-sm leading-6 text-text-primary">
            <section>
              <h3 className="text-sm font-semibold text-text-primary">What bulk import does</h3>
              <p className="mt-2 text-text-secondary">
                Bulk import helps you add new books or update existing books in one upload.
              </p>
              <p className="mt-2 text-text-secondary">
                For each row in your Excel file, Bukito decides:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-text-secondary">
                <li>is this book already in the library?</li>
                <li>or is this a new book?</li>
              </ul>
              <p className="mt-2 text-text-secondary">
                Then it builds the final book record, fills in any missing details it can find, and creates AI tags when enough information is available.
              </p>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-text-primary">If the book already exists</h3>
              <p className="mt-2 text-text-secondary">
                If Bukito finds an existing book, it updates that book.
              </p>
              <p className="mt-2 text-text-secondary">
                The current book in the database is used as the starting point, then:
              </p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-text-secondary">
                <li>Excel values are applied on top</li>
                <li>missing fields may be filled by enrichment</li>
                <li>AI tags are generated from the final result</li>
                <li>the book is updated</li>
              </ol>
              <p className="mt-3 text-text-secondary">
                Important:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-text-secondary">
                <li>if Excel provides a non-empty value, that value wins</li>
                <li>existing values are only kept when Excel leaves that field blank</li>
              </ul>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-text-primary">If the book is new</h3>
              <p className="mt-2 text-text-secondary">
                If Bukito does not find the book, it tries to build the new record using Google Books first.
              </p>
              <p className="mt-2 text-text-secondary">
                The flow is:
              </p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-text-secondary">
                <li>Google Books is used as the starting point, if found</li>
                <li>Excel values are applied on top</li>
                <li>missing fields may be filled by enrichment</li>
                <li>AI tags are generated from the final result</li>
                <li>the book is inserted into the library</li>
              </ol>
              <p className="mt-3 text-text-secondary">
                If Google Books cannot find the book:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-text-secondary">
                <li>the row can still be imported</li>
                <li>Bukito will use the Excel data it has</li>
                <li>import is not blocked just because Google Books has no match</li>
              </ul>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-text-primary">How Excel values are used</h3>
              <p className="mt-2 text-text-secondary">
                Excel is the main source of truth for any field you actually provide.
              </p>
              <p className="mt-2 text-text-secondary">
                That means:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-text-secondary">
                <li>if you enter a value in Excel, Bukito will use that value</li>
                <li>if a value already exists in the library, the Excel value will replace it</li>
                <li>for new books, Excel values replace Google Books values when both exist</li>
              </ul>
              <p className="mt-3 text-text-secondary">
                Examples:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-text-secondary">
                <li>if Excel includes a new description, that description is used</li>
                <li>if Excel includes a new illustrator, that illustrator is used</li>
                <li>if Excel includes a new cover URL, that cover URL is used</li>
              </ul>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-text-primary">What blank cells mean</h3>
              <p className="mt-2 text-text-secondary">Blank cells mean:</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-text-secondary">
                <li>&ldquo;do not change this field&rdquo;</li>
              </ul>
              <p className="mt-3 text-text-secondary">They do not mean:</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-text-secondary">
                <li>&ldquo;clear this field&rdquo;</li>
                <li>&ldquo;remove the current value&rdquo;</li>
              </ul>
              <p className="mt-3 text-text-secondary">
                So if a book already has a description and the Excel description cell is blank, the description stays as it is.
              </p>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-text-primary">How Google Books and enrichment work</h3>
              <p className="mt-2 text-text-secondary">
                Google Books is mainly used to help with new books.
              </p>
              <p className="mt-2 text-text-secondary">
                After Excel values are applied, Bukito may also try to fill in missing fields such as:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-text-secondary">
                <li>description</li>
                <li>cover</li>
                <li>illustrator</li>
                <li>reading age</li>
                <li>other helpful metadata</li>
              </ul>
              <p className="mt-3 text-text-secondary">
                Enrichment only fills fields that are still missing. It does not replace a non-blank Excel value.
              </p>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-text-primary">How AI tags V2 work</h3>
              <p className="mt-2 text-text-secondary">
                AI tags V2 are generated from the <strong>final version</strong> of the book record.
              </p>
              <p className="mt-2 text-text-secondary">
                This means tags use the best available description after:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-text-secondary">
                <li>Google Books</li>
                <li>Excel overrides</li>
                <li>enrichment fills</li>
              </ul>
              <p className="mt-3 text-text-secondary">
                If usable book metadata becomes available at any point in that row&rsquo;s flow, Bukito can create AI tags V2 in the same import run.
              </p>
              <p className="mt-2 text-text-secondary">
                If there is not enough book metadata, AI tags V2 may fall back to an empty V2 payload.
              </p>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-text-primary">Common expectations / common misunderstandings</h3>
              <div className="mt-3 space-y-3 text-text-secondary">
                <p><strong>&ldquo;I uploaded a blank cell, why didn&rsquo;t it erase the old value?&rdquo;</strong><br />Blank means &ldquo;not provided,&rdquo; not &ldquo;delete.&rdquo;</p>
                <p><strong>&ldquo;Why didn&rsquo;t Google Books overwrite my Excel value?&rdquo;</strong><br />Because Excel wins when you provide a non-blank value.</p>
                <p><strong>&ldquo;Can a row still import if Google Books does not find the book?&rdquo;</strong><br />Yes. A valid row can still be imported using Excel data only.</p>
                <p><strong>&ldquo;Do AI tags V2 use the first description found?&rdquo;</strong><br />No. They use the final resolved book data after merging is complete.</p>
                <p><strong>&ldquo;Can enrichment still help after Excel values are applied?&rdquo;</strong><br />Yes, but only for fields that are still missing.</p>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-text-primary">Quick examples</h3>
              <div className="mt-3 space-y-4 text-text-secondary">
                <div>
                  <p><strong>Example 1: Existing book</strong></p>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    <li>the book already exists</li>
                    <li>Excel includes a new description and illustrator</li>
                    <li>Bukito keeps the existing book as the base</li>
                    <li>Excel replaces description and illustrator</li>
                    <li>missing fields may be filled</li>
                    <li>AI tags V2 are regenerated from the final book data</li>
                    <li>the book is updated</li>
                  </ul>
                </div>

                <div>
                  <p><strong>Example 2: New book with Google Books match</strong></p>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    <li>the book does not exist</li>
                    <li>Google Books finds title, cover, and description</li>
                    <li>Excel includes a different description</li>
                    <li>Bukito starts with Google Books, then uses the Excel description</li>
                    <li>missing fields may still be filled</li>
                    <li>AI tags V2 are created from the final merged record</li>
                    <li>the book is inserted</li>
                  </ul>
                </div>

                <div>
                  <p><strong>Example 3: New book without Google Books match</strong></p>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    <li>the book does not exist</li>
                    <li>Google Books finds nothing</li>
                    <li>Excel still has title, author, and description</li>
                    <li>Bukito uses the Excel row as the record</li>
                    <li>AI tags V2 can still be created if the final metadata is usable</li>
                    <li>the book is inserted</li>
                  </ul>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkImportHelp;
